import { useEffect, useMemo } from 'react';

import { OrderOperationType, PermissionType } from 'api/types/entity';
import { OrderStatusType } from 'api/types/entity/order/orderStatusType';
import { isGlobalProject } from 'config';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate } from 'libs/navigation';
import { usePermissions } from 'libs/permissions';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';

import { useFormContext } from '../../forms/orderForm';
import { useToggleEditable } from '../../hooks/useToggleEditable';
import { useOrderContext } from '../../providers/OrderProvider';
import { OrderFormTab } from '../../types';

enum ActionType {
  edit = 'edit',
  changeStatus = 'changeStatus',
  addOperation = 'addOperation',
  deleteOrder = 'deleteOrder',
  downloadOrder = 'downloadOrder',
}

const finishedStatuses = [
  OrderStatusType.Cancelled,
  OrderStatusType.Executed,
  OrderStatusType.PartiallyExecuted,
  OrderStatusType.NotExecuted,
  OrderStatusType.Revoked,
];

const addOperationAvailableStatuses = [
  OrderStatusType.InProgress,
  OrderStatusType.AwaitingExecutionCheck,
  OrderStatusType.ExecutionCheck,
];

interface UseOrderFormHeaderProps {
  tab: OrderFormTab;
}

export const useOrderFormHeader = ({ tab }: UseOrderFormHeaderProps) => {
  const { t } = useTranslation();
  const { navigate } = useNavigate();
  const { checkPermissions } = usePermissions();
  const { enableEditable } = useToggleEditable();

  const { order, onChangeStatus, onDelete, onDownloadOrder } =
    useOrderContext();

  const { readOnly, resetForm } = useFormContext();

  const editable = !readOnly;

  const actions = useMemo(() => {
    const isUnsignedOrder = order.status === OrderStatusType.AwaitingSigning;

    const isPurchaseOrSale =
      order.operationType === OrderOperationType.SecuritiesPurchase ||
      order.operationType === OrderOperationType.SecuritiesSale;

    const isExecutionHistoryTab = tab === OrderFormTab.executionHistory;

    const canChangeStatus =
      checkPermissions([PermissionType.Order_UpdateStatus]) &&
      !finishedStatuses.includes(order.status);

    const canAddOperation =
      addOperationAvailableStatuses.includes(order.status) &&
      isPurchaseOrSale &&
      checkPermissions([PermissionType.Operation_CreateOneViaOrder]) &&
      isExecutionHistoryTab;

    const canEdit = isUnsignedOrder
      ? checkPermissions([PermissionType.Order_UpdateUnsignedOne])
      : isExecutionHistoryTab &&
        checkPermissions([PermissionType.Order_UpdateSignedOne]);

    const canDelete =
      isUnsignedOrder && checkPermissions([PermissionType.Order_DeleteOne]);

    const canDownload = isGlobalProject() && !isUnsignedOrder;

    return lodash.compact([
      canDownload && {
        value: ActionType.downloadOrder,
        label: t('common.exportPDF'),
        Icon: Icon.Download,
      },
      canChangeStatus && {
        value: ActionType.changeStatus,
        label: t('order.order.changeStatusLabel'),
        Icon: Icon.Repeat,
      },
      canEdit && {
        value: ActionType.edit,
        label: t('common.edit'),
        Icon: Icon.Edit,
      },
      canAddOperation && {
        value: ActionType.addOperation,
        label: t('order.order.addOperationLabel'),
        Icon: Icon.Plus,
      },
      canDelete && {
        value: ActionType.deleteOrder,
        label: t('common.delete'),
        Icon: Icon.Bucket,
        color: Colors.red500,
      },
    ]);
  }, [checkPermissions, order.operationType, order.status, t, tab]);

  const goToAddOperation = () => {
    navigate(
      ROUTES.operations.createOperation.fullPath,
      {},
      { search: { orderId: order.id } }
    );
  };

  const onClickOption = (action: ActionType) => {
    switch (action) {
      case ActionType.edit:
        enableEditable();
        break;

      case ActionType.changeStatus:
        onChangeStatus();
        break;

      case ActionType.addOperation:
        goToAddOperation();
        break;

      case ActionType.deleteOrder:
        onDelete();
        break;
      case ActionType.downloadOrder:
        onDownloadOrder?.();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!editable) {
      resetForm();
    }
  }, [editable, resetForm]);

  return {
    actions,
    editable,
    onClickOption,
  };
};
